import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { formatInsightDate, insightConfidenceColour, insightConfidenceLabel, insightSourceLabel } from './profile-insight';

let nextDialogId = 0;

// Shared Magic Insights evidence dialog: the full answer, confidence, question, commentary and cited sources for one
// response. The parent renders it while an insight is selected and removes it on (closed). The dialog moves focus to
// its close button, keeps Tab inside itself, closes on Escape or a backdrop click and returns focus to the trigger.
// Project [evidenceAnswer] content to replace the default "label: value" rows with a formatted answer.
@Component({
  selector: 'company-profile-evidence-dialog',
  templateUrl: './company-profile-evidence-dialog.component.html',
  styleUrls: ['./company-profile-evidence-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CompanyProfileEvidenceDialogComponent implements AfterViewInit, OnDestroy {
  @Input() heading = '';
  @Input() eyebrow = 'EVIDENCE & CONFIDENCE';
  @Input() subtitle = '';
  @Input() response: any = null;
  /** Default answer rows. Leave unset and project [evidenceAnswer] content to format the answer yourself. */
  @Input() detailRows: Array<{ label: string; value: string }> | null = null;
  @Input() answerHeading = 'Full answer';
  @Input() commentaryHeading = 'Analyst commentary';
  @Input() referencesHeading = 'Sources & quotes';
  /** Show each source's type, date and verification, and when the answer was generated. */
  @Input() detailedReferences = false;
  @Input() closeLabel = 'Close evidence';
  @Output() closed = new EventEmitter<void>();
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('dialog') dialog?: ElementRef<HTMLElement>;

  readonly headingId = `profile-evidence-title-${++nextDialogId}`;
  readonly confidenceLabel = insightConfidenceLabel;
  readonly confidenceColour = insightConfidenceColour;
  readonly sourceLabel = insightSourceLabel;
  readonly formatDate = formatInsightDate;
  private readonly trigger = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;

  ngAfterViewInit(): void {
    setTimeout(() => this.closeButton?.nativeElement.focus());
  }

  ngOnDestroy(): void {
    const trigger = this.trigger;
    if (trigger && typeof trigger.focus === 'function') setTimeout(() => trigger.focus());
  }

  get references(): any[] {
    return Array.isArray(this.response?.references) ? this.response.references : [];
  }

  safeUrl(value: unknown): string {
    const text = String(value || '').trim();
    return /^https?:\/\//i.test(text) ? text : '';
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.closed.emit();
  }

  /** Keep keyboard focus inside the dialog while it is open. */
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.dialog) return;
    const focusable = Array.from(this.dialog.nativeElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
